import parsePhoneNumber, {
  CountryCode,
  isValidPhoneNumber as _isValidPhoneNumber,
} from 'libphonenumber-js'
import { Person, User } from 'src/@types/graphql'
import { v4 as uuidv4 } from 'uuid'

export const getPersonIdentifierForConversation = (
  person: PersonType | Person | undefined,
) => {
  if (!person) {
    return ''
  }
  return person?.first_name && person?.last_name
    ? `${person.first_name} ${person.last_name}`
    : person?.phones?.[0]?.phone_number
      ? twillioToPhoneNumberFormat(person.phones[0]?.phone_number)
          ?.nationalFormat
      : person?.emails?.[0]?.address || ''
}

export enum Identifier_Type {
  Phone = 'phone',
  Email = 'email',
}

export const getIdentifierLetterForPersonCircle = (person: PersonType) => {
  return person?.first_name && person?.last_name
    ? person.first_name.charAt(0).toUpperCase()
    : person?.phones?.[0]?.phone_number
      ? Identifier_Type.Phone
      : Identifier_Type.Email
}

//this is so we can pass a backup string if they're not a db user
export const formatNoteUserName = (user: User | UserType, creator_name) =>
  user && (user.id != 1 || creator_name == null)
    ? formatUserName(user)
    : creator_name

export const formatUserName = (user: User | UserType) =>
  user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.email

export const formatPhoneNumber = (
  phoneNumber?: string,
  countryCode?: CountryCode | undefined,
  countryCodeInclusive?: Boolean,
) => {
  if (!phoneNumber) return ''
  const targetNumber = parsePhoneNumber(phoneNumber, countryCode || 'US')
  if (targetNumber?.isValid()) {
    if (countryCodeInclusive) {
      return `${targetNumber.countryCallingCode} ${targetNumber.format(
        'NATIONAL',
      )}` // +1 (213) 373-4253
    } else {
      return ` ${targetNumber.format('NATIONAL')}` // (213) 373-4253
    }
  } else {
    return phoneNumber
  }
}

export const isValidPhoneNumber = (
  phoneNumber: string,
  countryCode: CountryCode | undefined = 'US',
) => {
  if (!phoneNumber) return false
  return _isValidPhoneNumber(phoneNumber, countryCode)
}

/**
 * Validates if a phone number has the correct format for US numbers
 * Accepts empty values for optional fields
 */
export const isValidPhoneNumberFormat = (
  value: string | null | undefined,
): boolean => {
  // Allow empty values for optional fields
  if (!value || value === '+' || value === '+1' || value === '') {
    return true
  }

  // Check if it's a properly formatted US phone number
  const phoneRegex = /^\+1\d{10}$/
  return phoneRegex.test(value)
}

export const getCountryCode = (phoneNumber: string) => {
  const targetNumber = parsePhoneNumber(phoneNumber)
  return targetNumber?.country || ''
}

export const twillioToPhoneNumberFormat = (twillioNumber: string) => {
  if (twillioNumber) {
    const targetNumber = parsePhoneNumber(twillioNumber)
    return {
      countryCode: targetNumber?.country || '',
      nationalFormat: targetNumber?.formatNational() || '',
      internationalFormat: targetNumber?.formatInternational() || '',
    }
  }
  return {
    countryCode: '',
    nationalFormat: '',
    internationalFormat: '',
  }
}

export const safeNumber = (value?: number | String) => {
  const _number = Number(value)
  if (!isNaN(_number)) {
    return _number
  } else {
    return 0
  }
}

export const formatAmount = (number?: number | string | null) => {
  if (number) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    })
    return formatter.format(safeNumber(number)) /* $2,500.00 */
  }
  return '$0'
}

export const formatPercentage = (number: number | string) => {
  return Number(number).toLocaleString(undefined, {
    style: 'percent',
    minimumFractionDigits: 2,
  })
}

// return string if the value is string otherwise empty string (useful when string/React.element are expected vals)
export const safeString = (value) => {
  if (typeof value === 'string') return value
  return ''
}

export const arrayFromMuiSelect = (selectedValues: string | any[]) => {
  if (typeof selectedValues === 'string') {
    if (selectedValues) {
      const _arr = selectedValues.split(',')
      if (_arr?.length) {
        return _arr
      }
    }
    return []
  }
  return selectedValues
}

export const formatFileNameForUrl = (fileName: string) => {
  if (fileName) {
    return fileName.replace(/\s/g, '_')
  }
  return ''
}

export const randomId = () => Math.floor(Math.random() * 101)

export const generateUuid = () => uuidv4()

export const stripHTMLTags = (str) => {
  if (!str) return null
  str = str.toString()
  return str.replace(/(<([^>]+)>)/gi, '')
}

export const isHTML = (str) => {
  var a = document.createElement('div')
  a.innerHTML = str

  for (var c = a.childNodes, i = c.length; i--; ) {
    if (c[i].nodeType == 1) return true
  }

  return false
}

export const isEmpty = (v) => {
  let finalText = v
  if (isHTML(finalText)) {
    finalText = stripHTMLTags(v)
  }

  return Array.isArray(finalText)
    ? finalText.length === 0
    : !finalText || finalText === '' || (isHTML(v) && finalText.length === 1)
}

export const customValidate = (request) => {
  let errors = {}
  let formisValid = true

  request &&
    Object.keys(request).forEach((field) => {
      let value = request[field]
      if (typeof value !== undefined) {
        if (isEmpty(request[field])) {
          errors[field] = true
          formisValid = false
        }
      }
    })

  return {
    errors,
    formisValid,
  }
}

export const clearError = (errors, field, setErrors) => {
  if (errors[field]) {
    setErrors((current) => {
      const copy = { ...current }
      delete copy[field]
      return copy
    })
  }
}

export const noOp = () => {}

export const convertTimeToDateTime = (strTime) => {
  if (!strTime) return null
  const formattedTime = convertTo24HrsTime(strTime)
  const [hours, minutes] = formattedTime?.split(':') || []
  const now = new Date()
  const newDateTime = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    Number(hours),
    Number(minutes),
  )
  return newDateTime
}

export const convertTo24HrsTime = (strTime) => {
  if (!strTime) return null
  const [time, modifier] = strTime.split(' ')
  let [hours, minutes, seconds] = time.split(':')

  if (hours === '12') {
    hours = '0'
  }

  if (minutes === '00') {
    minutes = '0'
  }

  if (modifier === 'PM') {
    hours = parseInt(hours, 10) + 12
  }

  return `${hours}:${minutes}:${seconds}`
}

export const unEscape = (htmlStr) => {
  htmlStr = htmlStr.replace(/&lt;/g, '<')
  htmlStr = htmlStr.replace(/&gt;/g, '>')
  htmlStr = htmlStr.replace(/&quot;/g, '"')
  htmlStr = htmlStr.replace(/&#39;/g, "'")
  htmlStr = htmlStr.replace(/&amp;/g, '&')
  return htmlStr
}

export const formatMessage = (message) => {
  if (!message) return ''
  const unescapedMessage = unEscape(message)
  const formattedMessage = unescapedMessage.replace(/(?:\r\n|\r|\n)/g, '<br />')
  return formattedMessage
}

interface LinkGeneratorProp {
  url?: string
  sub_domain?: string
  ids?: string[]
}
export const integrationLinkGenerator = (payload: LinkGeneratorProp) => {
  const { url = '', sub_domain = '', ids = [''] } = payload
  let result = url
  if (sub_domain) {
    result = result.replace('sub_domain', sub_domain)
  }
  for (let index = 0; index < ids.length; index++) {
    const element = ids[index]
    result = result.replace('pms_id', element)
  }
  return result
}

export const formatNthNumber = (number?: number) => {
  if (!number) return ''
  if (number > 3 && number < 21) return `${number}th`
  switch (number % 10) {
    case 1:
      return `${number}st`
    case 2:
      return `${number}nd`
    case 3:
      return `${number}rd`
    default:
      return `${number}th`
  }
}

export const isValidURL = (url) => {
  const urlPattern = /^https?:\/\/\S+$/i
  return urlPattern.test(url)
}

export const dollarFormat = (number: number) => {
  if (!number && typeof number !== 'number') return ''

  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    style: 'currency',
  }).format(number)
}

export const snakeCase = (inputString) => {
  if (typeof inputString !== 'string' || inputString.trim() === '') {
    return '' // Return an empty string for invalid input or empty strings
  }

  const stringWithUnderscores = inputString.replace(/[-\s]/g, '_').toLowerCase()
  const lowercaseString = stringWithUnderscores.toLowerCase()
  return lowercaseString
}

const Intervals = {
  week: 'weekly',
  month: 'monthly',
  day: 'daily',
}

export const formatInterval = (interval) => Intervals[interval]

// formula injection prevention
export const sanitizeCSV = (cell) => {
  // Check if the cell content starts with any of '=', '+', '-', or '@'
  if (
    typeof cell === 'string' &&
    ['=', '+', '-', '@', '0x09', '0x0D'].includes(cell.charAt(0))
  ) {
    // Prepend a single quote (') to escape the cell content
    return `'${cell}`
  }
  return cell
}

export const pluralize = (word: string, count: number): string => {
  const pluralRules: { [key: string]: RegExp } = {
    irregular: /^(person|man|child|tooth|foot|mouse|goose|ox)$/i,
    specialPlural: /(?:[^aeiou]o|ch|sh|ss|zz)$/i,
    fEnding: /(?:[lr]f|[^aeiou]fe)$/i,
    yEnding: /(?:[^aeiou]y)$/i,
    general: /(.+)$/i,
  }

  const irregularPlurals: { [key: string]: string } = {
    person: 'people',
    man: 'men',
    child: 'children',
    tooth: 'teeth',
    foot: 'feet',
    mouse: 'mice',
    goose: 'geese',
    ox: 'oxen',
  }

  if (count === 1) {
    return word
  }

  if (pluralRules.irregular.test(word)) {
    return irregularPlurals[word.toLowerCase()]
  }

  if (pluralRules.specialPlural.test(word)) {
    return word + 'es'
  }

  if (pluralRules.fEnding.test(word)) {
    return word.replace(/f$/, 'ves').replace(/fe$/, 'ves')
  }

  if (pluralRules.yEnding.test(word)) {
    return word.replace(/y$/, 'ies')
  }

  if (pluralRules.general.test(word)) {
    return word + 's'
  }

  return word // Fallback to return the word itself if no rules apply
}
